// @flow
import { differenceInCalendarMonths } from 'date-functions';

import listInSeries from './listInSeries';

const groupConsecutiveMonths = (monthDates: Date[]): Date[][] =>
  monthDates
    .sort((a, b) => a.getTime() - b.getTime())
    .reduce((monthGroups: Date[][], monthDate: Date) => {
      if (monthGroups.length === 0) {
        monthGroups.push([monthDate]);
      } else {
        const previousMonthGroup = monthGroups[monthGroups.length - 1];
        const previousMonthDate = previousMonthGroup[previousMonthGroup.length - 1];
        const monthsApart = differenceInCalendarMonths(monthDate, previousMonthDate);
        if (monthsApart === 1) {
          previousMonthGroup.push(monthDate);
        } else {
          monthGroups.push([monthDate]);
        }
      }
      return monthGroups;
    }, []);

const separateGroupsWithTwoMonths = (monthGroups: Date[][]): Date[][] =>
  monthGroups.reduce((separatedMonthGroups, monthGroup) => {
    if (monthGroup.length === 2) {
      separatedMonthGroups.push([monthGroup[0]]);
      separatedMonthGroups.push([monthGroup[1]]);
    } else {
      separatedMonthGroups.push(monthGroup);
    }
    return separatedMonthGroups;
  }, []);

const describeMonthGroup = (
  monthGroup: Date[],
  formatMonth: Date => string,
  formatYear: Date => string,
): string => {
  const firstMonthInRange = monthGroup[0];
  const formattedStartMonth = formatMonth(firstMonthInRange);
  const formattedStartYear = formatYear(firstMonthInRange);
  if (monthGroup.length === 1) {
    return `${formattedStartMonth} ${formattedStartYear}`;
  }
  const lastMonthInRange = monthGroup[monthGroup.length - 1];
  const formattedEndMonth = formatMonth(lastMonthInRange);
  const isMultiYearRange = firstMonthInRange.getFullYear() !== lastMonthInRange.getFullYear();
  if (isMultiYearRange) {
    const formattedEndYear = formatYear(lastMonthInRange);
    return `${formattedStartMonth} ${formattedStartYear} to ${formattedEndMonth} ${formattedEndYear}`;
  }
  return `${formattedStartMonth} to ${formattedEndMonth} ${formattedStartYear}`;
};

export const getMonthName = (date: Date): string => date.toLocaleString('en-US', { month: 'long' });

export const getNumericYear = (date: Date): string =>
  date.toLocaleString('en-US', { year: 'numeric' });

const describeMonthGroups = (
  monthGroups: Date[][],
  formatMonth: Date => string,
  formatYear: Date => string,
): string => {
  // Separate month groupings that only have two months in them, otherwise they will be considered a
  // range, which looks bad, for example: "January to February" instead of "January and February"
  const monthGroupsWithoutPairs = separateGroupsWithTwoMonths(monthGroups);
  const monthGroupDescriptions = monthGroupsWithoutPairs.map(monthGroup =>
    describeMonthGroup(monthGroup, formatMonth, formatYear),
  );
  return listInSeries(monthGroupDescriptions);
};

/**
 * Takes an Array of Dates and returns a nicely formatted list of month names with years. Groupings
 * of consecutive months will be grouped together. Omits redundant years in groups that start and
 * end in the same year.
 *
 * For example, given
 * [
 *   '1999-12-01',
 *   '2000-01-01',
 *   '2000-02-01',
 *   '2000-04-01',
 *   '2000-05-01',
 *   '2000-06-01',
 *   '2000-09-01',
 * ]
 * returns: 'December 1999 to February 2000, April to June 2000, and September 2000'
 */
const describeMonths = (
  months: Date[],
  options: {
    formatMonth?: Date => string,
    formatYear?: Date => string,
  } = {},
): string => {
  const formatMonth = options.formatMonth || getMonthName;
  const formatYear = options.formatYear || getNumericYear;
  return describeMonthGroups(groupConsecutiveMonths(months), formatMonth, formatYear);
};

export default describeMonths;
